import React, { useState, useMemo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, TextInput, ActivityIndicator } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { webColors, webBorderRadius, webShadows } from '../../../../constants/webStyles';
import { getProfessionTypes } from '../../../../services/shared';

// Default profession data for fallback
const DEFAULT_PROFESSIONS = [
  {
    id: 'doctor',
    code: 'a',
    name: 'Doctor',
    icon: '🩺',
    iconBg: '#dbeafe',
    specializations: [
      { code: 'a-a', name: 'General Practitioner' },
      { code: 'a-b', name: 'Specialist' },
    ],
  },
  {
    id: 'nurse',
    code: 'b',
    name: 'Nurse',
    icon: '👨‍⚕️',
    iconBg: '#dcfce7',
    specializations: [
      { code: 'b-a', name: 'Registered Nurse' },
      { code: 'b-b', name: 'Enrolled Nurse' },
    ],
  },
  {
    id: 'dentist',
    code: 'c',
    name: 'Dentist',
    icon: '🦷',
    iconBg: '#fef3c7',
    specializations: [
      { code: 'c-a', name: 'General Dentist' },
      { code: 'c-b', name: 'Specialist Dentist' },
    ],
  },
  {
    id: 'dental-assistant',
    code: 'd',
    name: 'Dental Surgery Assistant',
    icon: '💎',
    iconBg: '#f3e8ff',
    specializations: [
      { code: 'd-a', name: 'Chairside Assistant' },
      { code: 'd-b', name: 'Administrative Assistant' },
    ],
  },
];

const StepProfessionWeb = ({ formData, updateFormData, goToStep }) => {
  const [expandedProfession, setExpandedProfession] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch profession types from API
  const { data: typesData, isLoading, error } = useQuery(
    ['professionTypes'],
    () => getProfessionTypes(),
    {
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
    }
  );

  // Organize API data into hierarchy
  const professions = useMemo(() => {
    const apiTypes = typesData?.data?.data || [];

    if (apiTypes.length === 0) {
      return DEFAULT_PROFESSIONS;
    }

    const hierarchy = {};
    const iconMapping = {
      'a': { icon: '🩺', iconBg: '#dbeafe' },
      'b': { icon: '👨‍⚕️', iconBg: '#dcfce7' },
      'c': { icon: '🦷', iconBg: '#fef3c7' },
      'd': { icon: '💎', iconBg: '#f3e8ff' },
      'e': { icon: '🏥', iconBg: '#e0f2fe' },
      'f': { icon: '📋', iconBg: '#fce7f3' },
      'g': { icon: '🔬', iconBg: '#eef2ff' },
      'h': { icon: '🏠', iconBg: '#ccfbf1' },
      'i': { icon: '📷', iconBg: '#ffedd5' },
      'j': { icon: '🔊', iconBg: '#ede9fe' },
      'k': { icon: '💊', iconBg: '#dcfce7' },
      'l': { icon: '🧪', iconBg: '#dbeafe' },
      'm': { icon: '🔧', iconBg: '#f1f5f9' },
      'n': { icon: '⚙️', iconBg: '#f4f4f5' },
      'o': { icon: '🏃', iconBg: '#e0f2fe' },
      'p': { icon: '🧠', iconBg: '#fae8ff' },
      'q': { icon: '⚕️', iconBg: '#d1fae5' },
      'r': { icon: '🩹', iconBg: '#f5f5f4' },
      's': { icon: '👔', iconBg: '#cffafe' },
      't': { icon: '🏢', iconBg: '#ede9fe' },
      'u': { icon: '🛠️', iconBg: '#fef9c3' },
    };

    apiTypes.forEach(type => {
      const code = type.code;

      // Skip types ending with -x (treated as standalone)
      if (code.endsWith('-x')) {
        hierarchy[code] = {
          id: code,
          code: code,
          name: type.english_name,
          icon: iconMapping[code.charAt(0)]?.icon || '🏥',
          iconBg: iconMapping[code.charAt(0)]?.iconBg || '#f3f4f6',
          specializations: []
        };
      }
      // Top-level types (no dash)
      else if (!code.includes('-')) {
        if (!hierarchy[code]) {
          hierarchy[code] = {
            id: code,
            code: code,
            name: type.english_name,
            icon: iconMapping[code]?.icon || '🏥',
            iconBg: iconMapping[code]?.iconBg || '#f3f4f6',
            specializations: []
          };
        } else {
          hierarchy[code].name = type.english_name;
        }
      }
      // Second-level types (subtypes)
      else if (code.split('-').length === 2 && !code.endsWith('-x')) {
        const parentCode = code.split('-')[0];
        if (!hierarchy[parentCode]) {
          hierarchy[parentCode] = {
            id: parentCode,
            code: parentCode,
            name: 'Other',
            icon: iconMapping[parentCode]?.icon || '🏥',
            iconBg: iconMapping[parentCode]?.iconBg || '#f3f4f6',
            specializations: []
          };
        }
        hierarchy[parentCode].specializations.push({
          code: code,
          name: type.english_name
        });
      }
    });

    return Object.values(hierarchy).filter(p => p.specializations.length > 0 || p.code.endsWith('-x'));
  }, [typesData]);

  const normalizedQuery = searchQuery.trim().toLowerCase();
  const searchActive = normalizedQuery.length > 0;

  const filteredProfessions = !searchActive
    ? professions
    : professions.reduce((acc, p) => {
        const nameMatches = p.name.toLowerCase().includes(normalizedQuery);
        const matchingSpecs = p.specializations.filter(s =>
          s.name.toLowerCase().includes(normalizedQuery)
        );
        if (nameMatches) {
          acc.push(p); // keep full category
        } else if (matchingSpecs.length > 0) {
          acc.push({ ...p, specializations: matchingSpecs }); // only matching specs
        }
        return acc;
      }, []);

  const selectSpecialization = (profession, specialization) => {
    updateFormData('profession', profession.code);
    updateFormData('professionName', profession.name);
    updateFormData('specialization', specialization.code);
    updateFormData('specializationName', specialization.name);
  };

  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={webColors.primary} />
        <Text style={styles.loadingText}>Loading profession types...</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.stepHeader}>
        <Text style={styles.stepIcon}>💼</Text>
        <Text style={styles.stepTitle}>Select Profession Type</Text>
        <Text style={styles.stepSubtitle}>Select your medical profession type</Text>
      </View>

      <View style={styles.searchWrapper}>
        <Text style={styles.searchIcon}>🔍</Text>
        <TextInput
          style={styles.searchInput}
          placeholder="Search Profession Type"
          placeholderTextColor={webColors.textMuted}
          value={searchQuery}
          onChangeText={setSearchQuery}
        />
      </View>

      <View style={styles.professionList}>
        {searchActive && filteredProfessions.length === 0 && (
          <Text style={styles.noResultsText}>No professions found</Text>
        )}
        {filteredProfessions.map((profession) => {
          const hasSpecializations = profession.specializations.length > 0;
          const isExpanded = searchActive ? true : expandedProfession === profession.id;

          return (
            <View key={profession.id}>
              <TouchableOpacity
                style={[
                  styles.professionItem,
                  isExpanded && styles.professionItemExpanded,
                ]}
                onPress={() => setExpandedProfession(
                  expandedProfession === profession.id ? null : profession.id
                )}
              >
                <View style={[styles.professionIcon, { backgroundColor: profession.iconBg }]}>
                  <Text style={styles.professionIconText}>{profession.icon}</Text>
                </View>
                <View style={styles.professionInfo}>
                  <Text style={[
                    styles.professionName,
                    isExpanded && styles.professionNameExpanded,
                  ]}>
                    {profession.name}
                  </Text>
                  {hasSpecializations && (
                    <Text style={styles.professionCount}>
                      {profession.specializations.length} specialization{profession.specializations.length !== 1 ? 's' : ''}
                    </Text>
                  )}
                </View>
                {hasSpecializations && (
                  <Text style={[
                    styles.professionArrow,
                    isExpanded && styles.professionArrowExpanded,
                  ]}>
                    ›
                  </Text>
                )}
              </TouchableOpacity>

              {isExpanded && hasSpecializations && (
                <View style={styles.specializationOptions}>
                  {profession.specializations.map((spec) => (
                    <TouchableOpacity
                      key={spec.code}
                      style={[
                        styles.specOption,
                        formData.specialization === spec.code && styles.specOptionSelected,
                      ]}
                      onPress={() => selectSpecialization(profession, spec)}
                    >
                      <View style={[
                        styles.specRadio,
                        formData.specialization === spec.code && styles.specRadioSelected,
                      ]}>
                        {formData.specialization === spec.code && (
                          <Text style={styles.specRadioCheck}>✓</Text>
                        )}
                      </View>
                      <Text style={[
                        styles.specText,
                        formData.specialization === spec.code && styles.specTextSelected,
                      ]}>
                        {spec.name}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              )}
            </View>
          );
        })}
      </View>

      <View style={styles.btnRow}>
        <TouchableOpacity style={styles.btnBack} onPress={() => goToStep(1)}>
          <Text style={styles.btnBackText}>Back</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.btnNext, !formData.specialization && styles.btnNextDisabled]}
          onPress={() => goToStep(3)}
          disabled={!formData.specialization}
        >
          <Text style={[styles.btnNextText, !formData.specialization && styles.btnNextTextDisabled]}>
            Next
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: webColors.textSecondary,
  },
  stepHeader: {
    marginBottom: 32,
  },
  stepIcon: {
    fontSize: 48,
    marginBottom: 16,
  },
  stepTitle: {
    fontSize: 28,
    fontWeight: '700',
    color: webColors.textPrimary,
    marginBottom: 8,
  },
  stepSubtitle: {
    fontSize: 16,
    color: webColors.primary,
  },
  searchWrapper: {
    position: 'relative',
    marginBottom: 20,
  },
  searchIcon: {
    position: 'absolute',
    left: 14,
    top: '50%',
    transform: [{ translateY: -9 }],
    fontSize: 16,
    zIndex: 1,
  },
  searchInput: {
    backgroundColor: webColors.backgroundAlt,
    borderWidth: 2,
    borderColor: webColors.border,
    borderRadius: webBorderRadius.medium,
    paddingHorizontal: 46,
    paddingVertical: 14,
    fontSize: 15,
    color: webColors.textPrimary,
  },
  professionList: {
    gap: 12,
    marginBottom: 24,
  },
  noResultsText: {
    fontSize: 14,
    color: webColors.textMuted,
    textAlign: 'center',
    paddingVertical: 24,
  },
  professionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    padding: 18,
    backgroundColor: webColors.cardBackground,
    borderWidth: 2,
    borderColor: webColors.border,
    borderRadius: webBorderRadius.large,
  },
  professionItemExpanded: {
    borderColor: webColors.secondaryLight,
    backgroundColor: '#eff6ff',
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  },
  professionIcon: {
    width: 48,
    height: 48,
    borderRadius: webBorderRadius.medium,
    alignItems: 'center',
    justifyContent: 'center',
  },
  professionIconText: {
    fontSize: 22,
  },
  professionInfo: {
    flex: 1,
  },
  professionName: {
    fontSize: 17,
    fontWeight: '600',
    color: webColors.textPrimary,
  },
  professionNameExpanded: {
    color: '#2563eb',
  },
  professionCount: {
    fontSize: 13,
    color: webColors.textMuted,
    marginTop: 3,
  },
  professionArrow: {
    fontSize: 20,
    color: webColors.textMuted,
    fontWeight: '300',
  },
  professionArrowExpanded: {
    transform: [{ rotate: '90deg' }],
    color: webColors.secondaryLight,
  },
  specializationOptions: {
    backgroundColor: '#eff6ff',
    borderWidth: 2,
    borderTopWidth: 0,
    borderColor: webColors.secondaryLight,
    borderBottomLeftRadius: webBorderRadius.large,
    borderBottomRightRadius: webBorderRadius.large,
    paddingHorizontal: 12,
    paddingBottom: 16,
  },
  specOption: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    padding: 16,
    backgroundColor: webColors.cardBackground,
    borderWidth: 2,
    borderColor: 'transparent',
    borderRadius: webBorderRadius.medium,
    marginTop: 8,
  },
  specOptionSelected: {
    borderColor: webColors.primary,
    backgroundColor: webColors.primaryLight,
  },
  specRadio: {
    width: 22,
    height: 22,
    borderRadius: 11,
    borderWidth: 2,
    borderColor: webColors.border,
    alignItems: 'center',
    justifyContent: 'center',
  },
  specRadioSelected: {
    borderColor: webColors.secondaryLight,
    backgroundColor: webColors.secondaryLight,
  },
  specRadioCheck: {
    color: '#ffffff',
    fontSize: 12,
    fontWeight: '700',
  },
  specText: {
    fontSize: 16,
    fontWeight: '500',
    color: webColors.textSecondary,
  },
  specTextSelected: {
    color: '#2563eb',
    fontWeight: '600',
  },
  btnRow: {
    flexDirection: 'row',
    gap: 16,
  },
  btnBack: {
    flex: 1,
    backgroundColor: webColors.backgroundAlt,
    paddingVertical: 16,
    borderRadius: webBorderRadius.medium,
    alignItems: 'center',
  },
  btnBackText: {
    color: webColors.textSecondary,
    fontSize: 16,
    fontWeight: '600',
  },
  btnNext: {
    flex: 2,
    backgroundColor: webColors.primary,
    paddingVertical: 16,
    borderRadius: webBorderRadius.medium,
    alignItems: 'center',
    ...webShadows.button,
  },
  btnNextDisabled: {
    backgroundColor: webColors.border,
    shadowOpacity: 0,
  },
  btnNextText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
  btnNextTextDisabled: {
    color: webColors.textMuted,
  },
});

export default StepProfessionWeb;
